import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { v4 as uuid } from 'uuid';
import { Active } from '@dnd-kit/core';
import { PageGroup } from '@alucio/aws-beacon-amplify/src/models';
import {
  DocumentVersionORM,
  isDocumentORM,
  isDocumentVersionORM,
  isFolderORM,
  isFolderItemORM,
  FolderORM,
} from 'src/types/types';
import { useCurrentUser } from 'src/state/redux/selector/user';
import { useAllDocumentsInstance, useAllPersonalDocuments } from 'src/state/redux/selector/document';
import { filterCollection } from 'src/state/redux/selector/common';
import documentQuery, { filters } from 'src/state/redux/document/query';
import {
  PayloadGroup,
  usePresentationBuilderState,
} from '../state/PresentationBuilderStateProvider';
import { ThumbnailDimensions, ThumbnailSize } from 'src/hooks/useThumbnailSize/useThumbnailSize';
import { useDocumentSearchV2 } from 'src/hooks/useDocumentSearchV2';
import { useDNADocumentFilters } from 'src/components/DNA/Document/DNADocumentFilters';
import { GroupDraft } from 'src/components/SlideSelector/PageGroupList';
import useThumbnailSelector from 'src/components/SlideSelector/useThumbnailSelector';
import { SlideMode, ThumbnailPage } from 'src/components/SlideSelector/SlideSelector';
import { TargetItem } from 'src/components/DnD/DnDWrapper';
import { ContentORMs } from 'src/components/DNA/types';
import useContentPageData, { ContentPageData } from 'src/hooks/useContentPageData/useContentPageData';
import { getPresentable } from 'src/state/context/ContentProvider/helper';
import { useDNAFolderNav, withDNAFolderNav } from 'src/components/DNA/Folder/Nav/DNAFolderNav';
import { sortGroups } from 'src/utils/groupHelpers';
import { canSelectIndividualSlides } from 'src/utils/customDeckRules/eligibility';
import {
  addAssociatedSlides,
  addRequiredSlides,
  filterSelectedSlides,
} from 'src/utils/customDeckRules/selection';
import {
  countSystemAddedSlides,
  materializeDraftGroups,
} from 'src/utils/customDeckRules/payload';

export interface CurrentTab {
  [x: string]: {
      items: any;
      label: string;
  };
}

interface PresentationSelectorContextValue {
  currentTab: CurrentTab,
  filterDocuments: ReturnType<typeof useAllDocumentsInstance>,
  localSearchText: string,
  selectedTab: string,
  searchText: string,
  searchResultsText: string,
  isLoadingSearch: boolean,
  deckSearchText: string,
  setDeckSearchText: React.Dispatch<React.SetStateAction<string>>,
  selectedDocumentVersionORM?: DocumentVersionORM,
  isSelectionMode: boolean,
  allPagesSelected: boolean,
  pages: ThumbnailPage[],
  pageGroups: PageGroup[],
  selectedGroups: PayloadGroup[],
  selectorThumbnailSize: ThumbnailSize,
  selectorThumbnailDimensions: ThumbnailDimensions,
  groupsWithSomeSlidesSelected: PageGroup[],
  setLocalSearchText: React.Dispatch<React.SetStateAction<string>>,
  setSelectedTab: React.Dispatch<React.SetStateAction<string>>,
  setSearchText: React.Dispatch<React.SetStateAction<string>>,
  setSelectedDocumentVersionORM: React.Dispatch<React.SetStateAction<DocumentVersionORM | undefined>>,
  handleSelectAllToggle: (checked?: boolean) => void,
  handleThumbSelect: (thumbPage: ThumbnailPage) => void,
  setPageGroups: React.Dispatch<React.SetStateAction<PageGroup[]>>,
  handleGroupAdd: (insertingIndex?: number) => void,
  handleSelectGroup: (groupDraft: GroupDraft, forceSelect?: boolean) => void,
  cycleSelectorThumbnailSize: () => void,
  getGroupDrafts: () => GroupDraft[],
  getAllGroups: () => GroupDraft[],
  getAllGroupedIndividualSlides: () => GroupDraft[],
  poolItems: string[],
  targetItems: string[],
  selectedItemIds: string[],
  rearrangeEditorSlides: (targetItems: TargetItem[], lastActive?: Active) => void,
  active: Active | undefined,
  setActive: React.Dispatch<React.SetStateAction<Active | undefined>>,
  contentPageData?: ContentPageData[],
  onItemPress: (item: ContentORMs) => void,
  clearNavStack: () => void,
  popToFolder: (folderORM?: FolderORM) => void,
  isCurrentlyNested: boolean,
  folderStack: FolderORM[],
}

export const Options = {
  BOOKMARKS: 'Favorite Files',
  MY_FOLDERS: 'My Folders',
  LIBRARY: 'Library',
  MY_UPLOADS: 'My Uploads',
  SEARCH_RESULTS: 'Search results',
};

const BOOKMARKED_DOCS_QUERY = documentQuery.merge(
  documentQuery.filters.published,
  documentQuery.filters.bookmarked,
  documentQuery.filters.hidingEnabled,
  documentQuery.sorts.bookmarkedDesc,
)

const ALL_DOCS_QUERY = documentQuery.merge(
  documentQuery.sorts.updatedAtDesc,
  documentQuery.sorts.titleAsc,
  documentQuery.filters.published,
  documentQuery.filters.hidingEnabled,
  documentQuery.sorts.bookmarkedDesc,
)

export const PresentationSelectorStateContext = createContext<PresentationSelectorContextValue>(null!)
PresentationSelectorStateContext.displayName = 'PresentationSelectorContext'

const PresentationSelectorStateProvider: React.FC<PropsWithChildren> = (props) => {
  const [localSearchText, setLocalSearchText] = useState<string>('');
  const [searchText, setSearchText] = useState<string>('');
  const [deckSearchText, setDeckSearchText] = useState<string>('');
  const [selectedDocumentVersionORM, setSelectedDocumentVersionORM] = useState<DocumentVersionORM | undefined>();
  const presentable = getPresentable(selectedDocumentVersionORM, undefined, undefined, undefined, true);
  const { contentPageData } = useContentPageData(presentable, {
    disableCache: true,
    pagesJsonNetworkFirst: true,
    includeHiddenContentPages: true,
  });
  const {
    setAllContentPageDataVersions,
    builderMode,
    selectorThumbnailDimensions,
    selectorThumbnailSize,
    customDeck,
    selectedGroups,
    setSelectedGroups,
    cycleSelectorThumbnailSize,
    editorType,
    setNumOfRequiredSlides,
    associatedParentsMap,
    setAssociatedParentsMap,
    selectedTargetItems,
    targetFolder,
    setSelectedTargetItems,
    getPageWithDocumentVersionHiddenState,
  } = usePresentationBuilderState();
  const [selectedTab, setSelectedTab] = useState<string>(targetFolder ? Options.MY_FOLDERS : Options.BOOKMARKS);

  const thumbnailSelectorConfig = useMemo(() => {
    return {
      mode: SlideMode.DocumentVersion,
      disableRequiredToggle: false,
    }
  }, [])
  const {
    allPagesSelected,
    pages,
    pageGroups,
    groupsWithSomeSlidesSelected,
    handleSelectAllToggle,
    handleThumbSelect,
    setPageGroups,
    handleSelectGroup,
    getGroupDrafts,
    getAllGroups,
    getAllGroupedIndividualSlides,
    initializeThumbnails,
  } = useThumbnailSelector(thumbnailSelectorConfig, true)

  const isSelectionMode = builderMode === 'selection';

  const bookmarkedDocs = useAllDocumentsInstance(BOOKMARKED_DOCS_QUERY)
  const { filterSelectorOpts } = useDNADocumentFilters();
  const libraryQuery = useMemo(() => documentQuery.merge(ALL_DOCS_QUERY, filterSelectorOpts), [filterSelectorOpts]);
  const eligibleDocuments = useAllDocumentsInstance(ALL_DOCS_QUERY)
  const filteredLibraryDocuments = useAllDocumentsInstance(libraryQuery)
  const documents = useMemo(() => {
    // Filter queries can replace the version predicate that enforces slide-selection permissions.
    const allowedIds = new Set(eligibleDocuments.map(document => document.model.id));
    return filteredLibraryDocuments.filter(document => allowedIds.has(document.model.id));
  }, [eligibleDocuments, filteredLibraryDocuments]);
  const myPersonalDocuments = useAllPersonalDocuments()
  const {
    currentItems,
    pushFolder,
    popToFolder,
    folderStack,
    isCurrentlyNested,
  } = useDNAFolderNav()

  const filteredFolderItems = useMemo(() => {
    return currentItems.filter(item => {
      if (
        isDocumentVersionORM(item.relations.itemORM) &&
        item.relations.itemORM.meta.permissions.MSLNonModifiable
      ) return false
      else return true
    })
  }, [currentItems])

  const {
    documentORMSearchResults: resultsFromSearch,
    isLoadingSearch,
    query: searchQuery,
  } = useDocumentSearchV2(searchText, 'CUSTOM', filters.published, filterSelectorOpts);

  const filterDocuments = useMemo(() => selectedTab === Options.SEARCH_RESULTS
    ? filterCollection(eligibleDocuments, searchQuery ?? undefined)
    : eligibleDocuments,
  [eligibleDocuments, searchQuery, selectedTab]);

  const currentTab: CurrentTab = useMemo(() => {
    return {
      [Options.BOOKMARKS]: {
        items: bookmarkedDocs,
        label: 'Favorite Files',
      },
      [Options.MY_FOLDERS]: {
        items: filteredFolderItems,
        label: 'My Folders',
      },
      [Options.LIBRARY]: {
        items: documents,
        label: 'Library',
      },
      [Options.MY_UPLOADS]: {
        items: myPersonalDocuments,
        label: 'My Uploads',
      },
      // "Ghost tab". It works as a tab but is not shown as an option to be selected.
      // Will be applied upon a valid search text input value.
      [Options.SEARCH_RESULTS]: {
        items: resultsFromSearch.filter((document) =>
          document.relations.version.latestPublishedDocumentVersionORM?.meta.permissions.MSLSelectSlides,
        ),
        label: 'Search results',
      },
    }
  }, [bookmarkedDocs, filteredFolderItems, documents, myPersonalDocuments, resultsFromSearch])

  const calculateSelectionForDraft = (groupDrafts: GroupDraft[]) => {
    const selectedDrafts = filterSelectedSlides(groupDrafts)
    const associated = addAssociatedSlides(selectedDrafts, pages, selectedGroups, associatedParentsMap)
    const groupsForProcessing = addRequiredSlides(
      associated.groups,
      pages,
      selectedGroups,
      selectedDocumentVersionORM,
    )
    setAssociatedParentsMap(associated.associatedParentsMap)

    const result = materializeDraftGroups(
      groupsForProcessing,
      pages,
      selectedDocumentVersionORM!,
      {
        createId: uuid,
        getPageWithDocumentVersionHiddenState,
      },
    )

    result.forEach(group => {
      group.pages.forEach(page => {
        analytics?.track('CUSTOM_SLIDE_ADDED', {
          action: 'SLIDE_ADDED',
          category: 'CUSTOM',
          customDeckId: customDeck?.model.id,
          groupId: group.id,
          pageId: page.pageId,
          editorType: editorType,
        })
      })

      // ANALYTIC TRACKING: named group added
      if (group.pages.length > 1) {
        analytics?.track('GROUP_ADDED', {
          customDeckId: customDeck?.model.id,
          srcGroupId: group.groupSrcId,
        })
      }
    })

    return result
  }

  const searchResultsText: string = useMemo(() => {
    if (localSearchText === '') {
      return `${currentTab[selectedTab].items.length} item(s)`;
    }

    return `Search results for "${localSearchText}" | ${currentTab[selectedTab].items.length} result(s)`;
  }, [localSearchText, currentTab, selectedTab]);

  /**
   * Inserts new slides into a custom deck at the specified index.
   * This function allows for the addition of one or more new slides
   * into a custom deck at a designated position. If `insertingIndex` is not provided,
   * the slides will be added at the end of the deck by default.
   * @param {number | undefined} insertingIndex - The zero-based index at which to insert the new slides. If `undefined`, the slides will be added to the end of the deck.
   */
  const handleGroupAdd = (insertingIndex?: number) => {
    const allGroupDrafts = getGroupDrafts()
    const newPayloadGroups = calculateSelectionForDraft(allGroupDrafts)

    // Step 1: Calculate the required slides and associated slides, add them if they're not selected already
    const allRequiredNum = countSystemAddedSlides(newPayloadGroups)

    // Step 2: Uncheck all groups and set number of required slides added by system and not user
    setPageGroups(p => {
      return p.map(group => {
        return { ...group, checked: false }
      })
    })
    setNumOfRequiredSlides(allRequiredNum)

    // Step 3: insert or add slides to selectedGroup (groups in presentation editor)
    if (typeof insertingIndex !== 'undefined') {
      setSelectedGroups(p => {
        const front = p.slice(0, insertingIndex)
        const back = p.slice(insertingIndex)
        return [...front, ...newPayloadGroups, ...back]
      })
    }
    else setSelectedGroups(p => [...p, ...newPayloadGroups])

    // Step 4: unselect / uncheck all individual slides after adding to presentation
    handleSelectAllToggle(false); // uncheck all slides in presentation selector
    setSelectedTargetItems([]); // uncheck all slides in presentation editor

    // Step 5: Add the contentPageData into allContentPageDataVersions if it is not in there yet (for rendering slide title)
    const contentPageDataDocVerId: string | undefined =
      contentPageData[0]?.pageId.slice(0, contentPageData[0].pageId.lastIndexOf('_'));
    if (
      selectedDocumentVersionORM &&
      contentPageData.length &&
      contentPageDataDocVerId === selectedDocumentVersionORM.model.id
    ) {
      setAllContentPageDataVersions(preState => {
        if (preState[selectedDocumentVersionORM?.model.id]) return preState;
        else {
          return {
            ...preState,
            [selectedDocumentVersionORM?.model.id]: contentPageData,
          }
        }
      })
    }
  }

  //  we are recusrively adding the parent in case in the future we have more than 2 levels of nested documents
  const getFolderHierarchy = (folderORM: FolderORM, folderStack: FolderORM[] = []): FolderORM[] => {
    if (folderORM.relations.parentFolderORM) {
      return getFolderHierarchy(folderORM.relations.parentFolderORM, [folderORM, ...folderStack])
    }
    return [folderORM, ...folderStack]
  }

  useEffect(() => {
    if (targetFolder) {
      const folderStack = getFolderHierarchy(targetFolder)
      folderStack.forEach(stack => {
        pushFolder(stack)
      })
    }
  }, [])

  useEffect(() => {
    selectedDocumentVersionORM && initializeThumbnails(selectedDocumentVersionORM, false)
  }, [selectedDocumentVersionORM])

  useEffect(() => {
    setDeckSearchText('')
  }, [selectedDocumentVersionORM])

  // For Drag and Drop Feature
  const currentUser = useCurrentUser()
  const poolItems = useMemo(() => {
    if (!selectedDocumentVersionORM) return []

    const groupIds: string[] = selectedDocumentVersionORM.relations.pageGroups
      .slice()
      .sort(sortGroups()).map(pageGroup => pageGroup.model.id)

    if (!canSelectIndividualSlides(selectedDocumentVersionORM, currentUser.authProfile?.attributes.email)) {
      return groupIds;
    }

    const singleSlideIds: string[] = selectedDocumentVersionORM.relations.pages.map(page => page.model.pageId)
    return [...groupIds, ...singleSlideIds]
  }, [selectedDocumentVersionORM, currentUser])
  const targetItems = useMemo(() => selectedGroups.map(group => group.id), [selectedGroups])
  // selectedItemIds is an array of currently checked item's ids, it can contains slide ids and group ids.
  const selectedItemIds = useMemo(() => {
    const checkedGroupIds = pageGroups
      .filter(group => group.checked)
      .sort(sortGroups())
      .map(group => group.id)
    const checkedPageIds = pages
      .filter(page => page.checked)
      .map(page => page.pageId)
      .sort((a, b) => a.localeCompare(b, 'en', { numeric: true }))
    return [...checkedGroupIds, ...checkedPageIds]
  }, [pages, pageGroups])

  const rearrangeEditorSlides = (targetItems: TargetItem[], lastActive?: Active) => {
    if (targetItems.length !== selectedGroups.length) return;

    const selectedGroupsMap = new Map(selectedGroups.map(group => [group.id, group]))
    const targetItemsItemId  = targetItems.map(({ itemId }) => itemId)

    const activeElement = lastActive?.data?.current?.itemId as string
    let newIndex = targetItemsItemId.findIndex((id) => id === activeElement)
    if (newIndex === -1) return;

    const selectedTargetItemsWithoutActive = selectedTargetItems.filter((id) => id !== activeElement)
    const newGroupOrder = targetItemsItemId.filter((id) => !selectedTargetItemsWithoutActive.includes(id))

    // the index should be recalculate after removing the active element
    newIndex = newGroupOrder.findIndex((id) => id === activeElement)
    newGroupOrder.splice(newIndex + 1, 0, ...selectedTargetItems.filter((id) => id !== activeElement))
    const oldIndex = selectedGroups.findIndex((group) => group.id === activeElement)

    if (oldIndex === newIndex) return;

    const resultGroups = newGroupOrder.reduce<PayloadGroup[]>((acc, id) => {
      const group = selectedGroupsMap.get(id)
      if (group) acc.push(group)
      return acc
    }, [])

    setSelectedGroups(resultGroups)

    // Clear editor selection (uncheck checkboxes)
    setSelectedTargetItems([])
  }

  const [active, setActive] = useState<Active>()

  const onItemPress = (item: ContentORMs) => {
    if (isFolderORM(item)) {
      pushFolder(item)
    }
    else if (isFolderItemORM(item) && isDocumentVersionORM(item.relations.itemORM)) {
      const documentORM = item.relations.itemORM.relations.documentORM
      const updatedItem = documentORM.relations.version.latestPublishedDocumentVersionORM
      setSelectedDocumentVersionORM(updatedItem)
    }
    else if (isDocumentORM(item)) {
      const updatedItem = item.relations.version.latestPublishedDocumentVersionORM
      setSelectedDocumentVersionORM(updatedItem)
    }
  }

  function clearNavStack() {
    popToFolder();
    setSelectedDocumentVersionORM(undefined);
  }

  const contextValue: PresentationSelectorContextValue = {
    currentTab,
    filterDocuments,
    localSearchText,
    selectedTab,
    searchText,
    searchResultsText,
    isLoadingSearch,
    deckSearchText,
    setDeckSearchText,
    selectedDocumentVersionORM,
    isSelectionMode,
    allPagesSelected,
    pages,
    pageGroups,
    selectedGroups,
    selectorThumbnailSize,
    selectorThumbnailDimensions,
    groupsWithSomeSlidesSelected,
    setLocalSearchText,
    setSelectedTab,
    setSearchText,
    setSelectedDocumentVersionORM,
    handleSelectAllToggle,
    handleThumbSelect,
    setPageGroups,
    handleGroupAdd,
    handleSelectGroup,
    cycleSelectorThumbnailSize,
    getGroupDrafts,
    getAllGroups,
    getAllGroupedIndividualSlides,
    poolItems,
    targetItems,
    selectedItemIds,
    rearrangeEditorSlides,
    active,
    setActive,
    contentPageData,
    onItemPress,
    clearNavStack,
    popToFolder,
    isCurrentlyNested,
    folderStack,
  }

  return (
    <PresentationSelectorStateContext.Provider value={contextValue}>
      {props.children}
    </PresentationSelectorStateContext.Provider>
  )
}

export function usePresentationSelector() {
  const context = useContext(PresentationSelectorStateContext)
  if (!context) {
    throw new Error('usePresentationSelector must be used within the PresentationSelectorStateProvider')
  }
  return context;
}

export default withDNAFolderNav(PresentationSelectorStateProvider)
